import classNames from 'classnames'
import { useRef, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'

import { PHONE_INPUT_MASK } from '@/constants'

import cn from '../Forms.module.scss'

interface PhoneInputProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  errors?: FieldErrors
  autoComplete?: string
  id?: string
}

const PREFIX_LENGTH = 6
const FIRST_DIGIT_MIN_INDEX = 6

const getCursorPositionByDigitCount = (val: string, digitCount: number) => {
  let foundDigits = 0
  let pos = PREFIX_LENGTH
  for (let i = 0; i < val.length; i++) {
    if (foundDigits >= digitCount) break
    if (/\d/.test(val[i])) foundDigits++
    pos = i + 1
  }
  return Math.max(pos, PREFIX_LENGTH)
}

export function PhoneInput<T extends FieldValues>({
  name,
  control,
  errors,
  autoComplete,
  id,
}: PhoneInputProps<T>) {
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value = '', ref } }) => {
        const formatRawValue = (raw: string) => {
          let digits = raw.replace(/\D/g, '')
          if (digits.startsWith('375')) {
            digits = digits.slice(3)
          } else if (digits.startsWith('80')) {
            digits = digits.slice(2)
          }
          digits = digits.slice(0, 9)
          if (digits.length === 0) return ''

          let result = '+375 ('
          let digitIndex = 0
          for (let i = 6; i < PHONE_INPUT_MASK.length; i++) {
            const maskItem = PHONE_INPUT_MASK[i]
            if (digitIndex >= digits.length) break
            if (maskItem instanceof RegExp) {
              if (maskItem.test(digits[digitIndex])) {
                result += digits[digitIndex]
                digitIndex++
              } else {
                digits =
                  digits.slice(0, digitIndex) + digits.slice(digitIndex + 1)
                i--
              }
            } else {
              result += maskItem
            }
          }
          while (result.length > 0 && !/\d/.test(result[result.length - 1])) {
            result = result.slice(0, -1)
          }
          return result === '+375 (' ? '' : result
        }

        const getMaskSuffix = (val: string): string => {
          const digits = val.replace(/\D/g, '').replace(/^375/, '')
          let digitCount = 0
          let suffix = ''
          for (let i = 6; i < PHONE_INPUT_MASK.length; i++) {
            const item = PHONE_INPUT_MASK[i]
            if (item instanceof RegExp) {
              if (digitCount < digits.length) digitCount++
              else suffix += '_'
            } else {
              if (digitCount >= digits.length) suffix += item
            }
          }
          return suffix
        }

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          const target = e.target as HTMLInputElement
          const start = target.selectionStart || 0
          const end = target.selectionEnd || 0
          const isRange = start !== end

          if (/\d/.test(e.key)) {
            const digitsOnly = target.value.replace(/\D/g, '')
            if (digitsOnly.length >= 12 && !isRange) {
              e.preventDefault()
              return
            }
          }

          if (e.key === 'Backspace' && !isRange) {
            if (start <= PREFIX_LENGTH) {
              e.preventDefault()
              return
            }
            e.preventDefault()
            const isDigitBefore = /\d/.test(value[start - 1])
            const digitIdxToDelete = isDigitBefore
              ? start - 1
              : [...value.slice(0, start)].reduce(
                  (acc, char, idx) => (/\d/.test(char) ? idx : acc),
                  -1
                )

            if (digitIdxToDelete >= FIRST_DIGIT_MIN_INDEX) {
              const rawDigits = value.replace(/\D/g, '')
              const digitPosInRaw = value
                .slice(0, digitIdxToDelete + 1)
                .replace(/\D/g, '').length
              const newDigits =
                rawDigits.slice(0, digitPosInRaw - 1) +
                rawDigits.slice(digitPosInRaw)
              const newValue = formatRawValue(newDigits)
              onChange(newValue)
              requestAnimationFrame(() => {
                const newPos = getCursorPositionByDigitCount(
                  newValue,
                  digitPosInRaw - 1
                )
                target.setSelectionRange(newPos, newPos)
              })
            }
          }

          if (
            ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
              e.key
            ) &&
            !e.shiftKey
          ) {
            e.preventDefault()
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              const safePos = start < 6 ? 6 : start
              target.setSelectionRange(safePos, safePos)
              return
            }
            const lastDigitIndex = [...value].reduce(
              (acc, char, idx) => (/\d/.test(char) ? idx : acc),
              -1
            )
            if (e.key === 'ArrowLeft') {
              if (start <= 6) {
                target.setSelectionRange(6, 6)
                return
              }
              let pos = start - 1
              while (pos > 6 && !/\d/.test(value[pos - 1])) pos--
              target.setSelectionRange(pos, pos)
            } else if (e.key === 'ArrowRight') {
              if (start > lastDigitIndex) return
              let pos = start + 1
              while (pos <= lastDigitIndex + 1 && !/\d/.test(value[pos - 1]))
                pos++
              target.setSelectionRange(pos, pos)
            }
          }
        }

        const formattedValue = formatRawValue(value)
        const displayValue = formattedValue || (isFocused ? '+375 (' : '')

        return (
          <div className={cn.phoneInputContainer}>
            <div
              className={classNames(
                cn.phoneBackground,
                cn.phoneBackgroundPassive,
                errors?.[name] && cn.phoneBackgroundInvalid
              )}
            >
              {displayValue ? (
                <>
                  <span className={cn.phoneValue}>{displayValue}</span>
                  <span className={cn.phoneMask}>{getMaskSuffix(value)}</span>
                </>
              ) : (
                <span className={cn.phoneMask}>+375 (__) ___-__-__</span>
              )}
            </div>

            <input
              ref={(e) => {
                ref(e)
                inputRef.current = e
              }}
              value={displayValue}
              onFocus={() => {
                setIsFocused(true)
              }}
              onBlur={() => {
                setIsFocused(false)
                if (value.replace(/\D/g, '').length <= 3) onChange('')
                onBlur()
              }}
              onChange={(e) => {
                const input = e.target as HTMLInputElement
                const val = input.value
                const selStart = input.selectionStart || 0

                const isAutofill =
                  Math.abs(val.length - displayValue.length) > 1
                const formatted = formatRawValue(val)
                onChange(formatted)

                if (!isAutofill) {
                  const digitsBefore = val
                    .slice(0, selStart)
                    .replace(/\D/g, '').length
                  requestAnimationFrame(() => {
                    const newPos = getCursorPositionByDigitCount(
                      formatted,
                      digitsBefore
                    )
                    input.setSelectionRange(newPos, newPos)
                  })
                }
              }}
              onPaste={(e) => {
                e.preventDefault()
                const data = e.clipboardData.getData('text')
                if (data) onChange(formatRawValue(data))
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => {
                const target = e.target as HTMLInputElement
                if (target.selectionStart !== target.selectionEnd) return
                let pos = target.selectionStart || 0
                if (pos < 6) target.setSelectionRange(6, 6)
                else if (!/\d/.test(value[pos]) && !/\d/.test(value[pos - 1])) {
                  while (pos < value.length && !/\d/.test(value[pos])) pos++
                  target.setSelectionRange(pos, pos)
                }
              }}
              id={id}
              name={name}
              type="tel"
              inputMode="tel"
              autoComplete={autoComplete || 'tel'}
              className={
                errors?.[name]
                  ? [cn.phoneInput, cn.inputInvalid].join(' ')
                  : [cn.phoneInput, cn.inputValid].join(' ')
              }
            />
          </div>
        )
      }}
    />
  )
}
